import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import { Formik } from 'formik'
import { CustomTextField, SectionLayoutDialog } from 'shared'
import { StyledSelect } from 'shared/EditableMultipleSelection/EditableMultipleSelection.style'
import { loadEventInscriptionsByIdNumberAndCountry, resetValue } from 'state/modules/events'
import { STRING_EMPTY } from 'utils/constants'
import { getCountries } from 'utils/functions'
import { showSnackbarError } from 'utils/snackbar'
import * as Yup from 'yup'

import { useStyles } from './CheckStateInscriptionDialog.style'
import { InscriptionCard } from './components'

const ARGENTINA_COUNTRY = { label: 'Argentina', value: '01fa2de7-519f-4089-a33c-d48483ea0f20' }

const DIALOG_STATES = {
  SEARCH_INSCRIPTION_DIALOG: {
    value: 'SearchInscription',
    title: 'CONSULTAR ESTADO DE MI INSCRIPCIÓN'
  },
  STATE_INSCRIPTION_DIALOG: {
    value: 'StateInscription',
    title: 'ESTADO DE MI INSCRIPCIÓN'
  }
}

const CheckStateInscriptionDialog = ({ open, onClose }) => {
  const classes = useStyles()
  const { event, eventInscriptionError, eventInscriptions } = useSelector((state) => state.events)
  const dispatch = useDispatch()

  const [stateDialog, setStateDialog] = useState(DIALOG_STATES.SEARCH_INSCRIPTION_DIALOG)

  const handleOnClose = () => {
    onClose()
    dispatch(resetValue('eventInscription'))
    setStateDialog(DIALOG_STATES.SEARCH_INSCRIPTION_DIALOG)
  }

  useEffect(() => {
    if (eventInscriptionError) showSnackbarError(eventInscriptionError)

    return () => dispatch(resetValue('eventInscriptionError'))
  }, [eventInscriptionError])

  const renderSearchInscription = () => {
    const values = {
      country: ARGENTINA_COUNTRY,
      idNumber: STRING_EMPTY
    }

    const validationSchema = Yup.object().shape({
      idNumber: Yup.string()
        .required('Debe ingresar el DNI')
        .min(6, 'El DNI debe poseer como mínimo 6 caracteres')
        .max(15, 'El DNI debe poseer como máximo 15 caracteres')
        .matches(/^[a-z0-9]+$/i, 'El DNI no debe tener caracteres especiales ni espacios')
    })

    const searchInscription = async ({ country, idNumber }) => {
      const payload = {
        countryId: country.value,
        countryName: country.label,
        idNumber: idNumber.trim(),
        inscriptionFormId: event.activeInscriptionFormId
      }

      const eventInscriptionsSuccess = await dispatch(
        loadEventInscriptionsByIdNumberAndCountry(payload)
      )

      if (eventInscriptionsSuccess) setStateDialog(DIALOG_STATES.STATE_INSCRIPTION_DIALOG)
    }

    return (
      <Formik
        enableReinitialize
        initialValues={values}
        validationSchema={validationSchema}
        onSubmit={searchInscription}>
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          isValid
        }) => (
          <form className={classes.mainContainer} onSubmit={handleSubmit}>
            <div className={classes.formGroup}>
              <Typography color='primary' variant='h6'>
                País
              </Typography>
              <StyledSelect
                cacheOptions
                defaultOptions
                colorOptions='secondary'
                className={classes.select}
                onChange={(option) => setFieldValue('country', option)}
                loadOptions={getCountries()}
                placeholder='Seleccione una opción'
                maxMenuHeight={185}
                value={values.country}
              />
            </div>
            <div className={classes.formGroup}>
              <Typography className={classes.label} color='primary' variant='h6'>
                Ingresá tu DNI
              </Typography>
              <CustomTextField
                variant='outlined'
                name='idNumber'
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.idNumber && Boolean(errors.idNumber)}
                helperText={errors.idNumber}
                value={values.idNumber}
                className={classes.textField}
                placeholder='Ingresá tu número de DNI sin puntos y espacios'
              />
              {!event.isFree && (
                <Typography color='primary' variant='caption' className={classes.helperText}>
                  *Si realizaste el pago con MP tené en cuenta que puede demorar 1 hora en impactar
                  en el estado de tu inscripción.
                </Typography>
              )}
            </div>
            <Button
              className={classes.button}
              color='primary'
              type='submit'
              variant='contained'
              disabled={!isValid || isSubmitting}
              endIcon={isSubmitting && <CircularProgress color='primary' size={16} />}>
              Consultar
            </Button>
          </form>
        )}
      </Formik>
    )
  }

  const renderStateInscriptions = () => {
    const firstInscription = eventInscriptions[0] || {}

    const { firstName, lastName, idNumber } = firstInscription

    return (
      <div className={classes.inscriptionsContainer}>
        <Typography variant='h6' align='center'>
          {firstName} {lastName} - DNI {idNumber}
        </Typography>
        {eventInscriptions.map((x) => (
          <InscriptionCard key={x.id} eventInscription={x} />
        ))}
      </div>
    )
  }

  return (
    <SectionLayoutDialog
      open={open}
      onClose={handleOnClose}
      title={stateDialog.title}
      className={classes.dialog}
      contentClassName={classes.container}>
      {stateDialog === DIALOG_STATES.SEARCH_INSCRIPTION_DIALOG && renderSearchInscription()}
      {stateDialog === DIALOG_STATES.STATE_INSCRIPTION_DIALOG && renderStateInscriptions()}
    </SectionLayoutDialog>
  )
}
export default CheckStateInscriptionDialog
